import { useEffect, useRef } from 'react'
import { subscribe, unsubscribe, isLiveQuotesAvailable } from '../api/liveQuotes'

/**
 * Register the given symbols for live Schwab Streamer updates while this
 * component is mounted. Inbound ticks are written directly into the React
 * Query cache (['quote', symbol, ...] and ['active-quotes', symbolKey])
 * so callers don't need to read from a separate source.
 *
 * No-op when the user has no Schwab attachment.
 */
export function useLiveQuote(symbols: string[]): void {
  // Stable serialised key so React doesn't re-run on every render that
  // produces an equivalent array.
  const key = [...symbols].filter(Boolean).sort().join(',')
  const currentRef = useRef<string[]>([])

  useEffect(() => {
    if (!isLiveQuotesAvailable()) return
    const next = key ? key.split(',') : []
    const prev = currentRef.current
    const added = next.filter((s) => !prev.includes(s))
    const removed = prev.filter((s) => !next.includes(s))
    if (added.length) subscribe(added)
    if (removed.length) unsubscribe(removed)
    currentRef.current = next
  }, [key])

  // Unmount-only teardown: unsubscribe whatever the ref currently holds.
  useEffect(() => {
    return () => {
      if (currentRef.current.length) {
        unsubscribe(currentRef.current)
        currentRef.current = []
      }
    }
  }, [])
}
